/**
 * SCRUM-1032 — the output decoder Purpose list, in families.
 *
 * A flat list of a dozen terms from the same domain gives the eye nothing to
 * navigate by: "Agitator" and "Booster Pump" and "Flushing" all look alike
 * until they are read one at a time, and the list is opened once per decoder
 * on a controller carrying hundreds. Grouping turns reading into aiming.
 *
 * The families are what the decoder drives:
 *
 * - Stations — the two purposes that make a decoder a station (SCRUM-1040).
 * - Pumps & Valves — what moves and gates the water.
 * - Fertigation & Filtration — what treats it.
 *
 * Tank TopUp Valve is deliberately left out of Pumps & Valves: it fills a
 * tank rather than driving irrigation, and grouping it by the word "valve"
 * put it where nobody looking for it would look.
 *
 * The purposes come from the server, so this cannot be exhaustive. Anything
 * unrecognised falls into "Other" at the end rather than disappearing: a new
 * purpose the API adds is still selectable the day it appears, just ungrouped.
 */

export const PURPOSE_GROUP_ORDER = [
  'Stations',
  'Pumps & Valves',
  'Fertigation & Filtration',
  'Other',
] as const

export type PurposeGroup = (typeof PURPOSE_GROUP_ORDER)[number]

/**
 * The group names above are identifiers — they key the map below and order the
 * list. What the user reads is the translation, looked up by the caller that
 * renders the heading.
 */
export const PURPOSE_GROUP_LABEL_KEY: Record<PurposeGroup, string> = {
  'Stations': 'controllersPurposeGroupStations',
  'Pumps & Valves': 'controllersPurposeGroupPumpsValves',
  'Fertigation & Filtration': 'controllersPurposeGroupFertigationFiltration',
  'Other': 'controllersPurposeGroupOther',
}

/**
 * Keyed loosely — the API is not consistent about hyphens or case ("Non
 * Irrigation" in one place, "Non-Irrigation" in another), and a purpose that
 * fails to match would silently leave its family.
 */
const GROUP_BY_PURPOSE: Record<string, PurposeGroup> = {
  irrigation: 'Stations',
  nonirrigation: 'Stations',
  pumpstart: 'Pumps & Valves',
  boosterpump: 'Pumps & Valves',
  mastervalve: 'Pumps & Valves',
  fertigation: 'Fertigation & Filtration',
  agitator: 'Fertigation & Filtration',
  filtration: 'Fertigation & Filtration',
  flushing: 'Fertigation & Filtration',
}

const normalise = (purpose: unknown) =>
  String(purpose ?? '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

export const purposeGroupOf = (purpose: string): PurposeGroup =>
  GROUP_BY_PURPOSE[normalise(purpose)] ?? 'Other'

/**
 * Options in group order. MUI starts a new heading each time `groupBy` returns
 * something different, so an unsorted list would repeat the same heading.
 * Within a group the server's own order is kept — it is the order every other
 * screen shows these in.
 */
export const sortPurposesByGroup = (purposes: string[]): string[] =>
  [...purposes].sort(
    (a, b) =>
      PURPOSE_GROUP_ORDER.indexOf(purposeGroupOf(a)) -
      PURPOSE_GROUP_ORDER.indexOf(purposeGroupOf(b)),
  )
